import { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { Badge, Button, Card, Text, Title } from '@tremor/react';
import { RiCheckLine, RiCheckDoubleLine } from '@remixicon/react';
import notificationsApi from '../../network/notifications';
import { Link } from 'react-router-dom';
import { getNotificationLink } from '../../utils/notifications';

const NOTIFICATION_TYPE_LABELS: Record<string, string> = {
  HOLIDAY_PAID_REQUEST: 'Paid Leave Request',
  HOLIDAY_UNPAID_REQUEST: 'Unpaid Leave Request',
  HOLIDAY_APPROVED: 'Leave Approved',
  HOLIDAY_REJECTED: 'Leave Rejected',
  CARRYOVER_DEADLINE_NUDGE: 'Carryover Reminder',
  ABSENCE_PATTERN_ALERT: 'Absence Pattern Alert',
};

export default function NotificationsPage() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [filter, setFilter] = useState<'all' | 'unread' | 'read'>('all');
  const [typeFilter, setTypeFilter] = useState('');
  const limit = 20;

  const readParam =
    filter === 'unread' ? 'false' : filter === 'read' ? 'true' : undefined;

  const { data, isLoading } = useQuery(
    ['notifications-paginated', page, filter, typeFilter],
    () =>
      notificationsApi.getNotificationsPaginated({
        page,
        limit,
        read: readParam,
        type: typeFilter || undefined,
      }),
    { keepPreviousData: true },
  );

  const { data: unreadData } = useQuery(
    'unread-count',
    notificationsApi.getUnreadCount,
    { refetchInterval: 30000 },
  );

  const notifications = data?.notifications || [];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / limit);
  const unreadCount = unreadData?.count || 0;

  const handleMarkAsRead = async (id: string) => {
    try {
      await notificationsApi.markAsRead(id);
    } catch (error) {
      console.warn('Failed to mark notification as read', error);
      return;
    }
    queryClient.invalidateQueries('notifications-paginated');
    queryClient.invalidateQueries('unread-count');
    queryClient.invalidateQueries('notifications');
  };

  const handleMarkAllAsRead = async () => {
    await notificationsApi.markAllAsRead();
    queryClient.invalidateQueries('notifications-paginated');
    queryClient.invalidateQueries('unread-count');
    queryClient.invalidateQueries('notifications');
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <Title>Notifications</Title>
          <Text>
            {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}
          </Text>
        </div>
        <Button
          variant="secondary"
          icon={RiCheckDoubleLine}
          onClick={handleMarkAllAsRead}
          disabled={unreadCount === 0}
        >
          Mark all as read
        </Button>
      </div>

      <div className="mb-4 flex items-center gap-3">
        <div className="flex rounded-lg border border-gray-200">
          {(['all', 'unread', 'read'] as const).map((f) => (
            <button
              key={f}
              onClick={() => {
                setFilter(f);
                setPage(0);
              }}
              className={`px-4 py-2 text-sm font-medium capitalize ${
                filter === f
                  ? 'bg-gray-100 text-gray-900'
                  : 'text-gray-500 hover:text-gray-700'
              } ${f === 'all' ? 'rounded-l-lg' : ''} ${
                f === 'read' ? 'rounded-r-lg' : ''
              }`}
            >
              {f}
              {f === 'unread' && unreadCount > 0 && (
                <span className="ml-1 rounded-full bg-red-100 px-2 py-0.5 text-xs text-red-600">
                  {unreadCount}
                </span>
              )}
            </button>
          ))}
        </div>

        <select
          className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700"
          value={typeFilter}
          onChange={(e) => {
            setTypeFilter(e.target.value);
            setPage(0);
          }}
        >
          <option value="">All types</option>
          {Object.entries(NOTIFICATION_TYPE_LABELS).map(([key, label]) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-t-2 border-red-500" />
        </div>
      ) : notifications.length === 0 ? (
        <Card className="py-8 text-center">
          <Text>No notifications found.</Text>
        </Card>
      ) : (
        <div className="space-y-2">
          {notifications.map((notification: any) => (
            <Card
              key={notification.id}
              className={`p-4 ${notification.read ? 'opacity-60' : ''}`}
            >
              <div className="flex items-start justify-between">
                {(() => {
                  const link = getNotificationLink(notification);
                  const content = (
                    <div className="flex items-start gap-3">
                      {!notification.read && (
                        <div className="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-red-500" />
                      )}
                      <div>
                        <Text className="font-medium text-gray-900">
                          {notification.name}
                        </Text>
                        <Text className="text-gray-600">
                          {notification.description}
                        </Text>
                        {notification.content && (
                          <Text className="mt-1 text-sm text-gray-400">
                            {notification.content}
                          </Text>
                        )}
                        <div className="mt-1 flex items-center gap-2">
                          <Text className="text-xs text-gray-400">
                            {notification.time}
                          </Text>
                          {notification.type && (
                            <Badge color="gray" size="xs">
                              {NOTIFICATION_TYPE_LABELS[notification.type] ||
                                notification.type}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                  return link ? (
                    <Link
                      to={link}
                      className="flex-1"
                      onClick={() => {
                        if (!notification.read) {
                          handleMarkAsRead(notification.id);
                        }
                      }}
                    >
                      {content}
                    </Link>
                  ) : (
                    <div className="flex-1">{content}</div>
                  );
                })()}
                {!notification.read && (
                  <Button
                    variant="light"
                    size="xs"
                    icon={RiCheckLine}
                    onClick={() => handleMarkAsRead(notification.id)}
                    tooltip="Mark as read"
                  />
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between">
          <Text className="text-sm text-gray-500">
            Showing {page * limit + 1}-{Math.min((page + 1) * limit, total)} of{' '}
            {total}
          </Text>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="xs"
              onClick={() => setPage(Math.max(0, page - 1))}
              disabled={page === 0}
            >
              Previous
            </Button>
            <Button
              variant="secondary"
              size="xs"
              onClick={() => setPage(page + 1)}
              disabled={page >= totalPages - 1}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
